// src/components/resources/calculators/MerCalculator.logic.ts

/**
 * Pure, framework-free MER (Marketing Efficiency Ratio) math — extracted from
 * `MerCalculator.tsx` so it can be unit-tested directly (A7/D4 / BP1). The
 * component imports `calculateMetrics` + `validateInputs` and renders; all
 * arithmetic and the (deterministic) recommendation/validation logic live here.
 */

export interface CalculatorInputs {
  totalRevenue: number
  totalMarketingSpend: number
  attributedRevenue: number
  newCustomerRevenue: number
  includeAdvancedMetrics: boolean
}

export interface CalculatorResults {
  mer: number
  amer: number
  nmer: number
  recommendations: Array<{
    type: 'primary' | 'optimization' | 'tip'
    text: string
    icon?: string
  }>
}

/**
 * Compute the three efficiency ratios (MER / aMER / nMER) and the
 * deterministic recommendation set derived from their relationships.
 */
export function calculateMetrics(inputs: CalculatorInputs): CalculatorResults {
  // Calculate the three efficiency ratios
  const mer = inputs.totalRevenue / inputs.totalMarketingSpend
  const amer = inputs.attributedRevenue / inputs.totalMarketingSpend
  const nmer = inputs.newCustomerRevenue / inputs.totalMarketingSpend

  // Generate recommendations based on the results
  const recommendations: Array<{
    type: 'primary' | 'optimization' | 'tip'
    text: string
    icon?: string
  }> = []

  // Primary recommendation
  recommendations.push({
    type: 'primary',
    text: `Your overall marketing efficiency (MER) is ${mer.toFixed(2)}x, meaning you generate $${mer.toFixed(2)} in revenue for every $1 spent on marketing.`,
    icon: 'chart'
  })

  // Check relationship between metrics for insights
  if (amer < 1.0 && mer > 2.0) {
    recommendations.push({
      type: 'optimization',
      text: 'Your attributed efficiency (aMER) is significantly lower than overall MER, suggesting strong organic growth or attribution gaps.',
      icon: 'attribution'
    })
  }

  if (nmer < 1.0) {
    recommendations.push({
      type: 'optimization',
      text: 'Your new customer acquisition efficiency (nMER) is below 1.0, indicating you may be spending more on acquisition than you receive in first purchase revenue.',
      icon: 'acquisition'
    })
  }

  if (nmer > mer) {
    recommendations.push({
      type: 'tip',
      text: 'Your new customer efficiency (nMER) exceeds your overall efficiency (MER), which is unusual. Double-check your new customer revenue calculation.',
      icon: 'warning'
    })
  }

  // General optimization tips
  if (mer < 3.0) {
    recommendations.push({
      type: 'tip',
      text: 'Consider optimizing your marketing mix to improve overall efficiency, focusing on channels with the highest return.',
      icon: 'strategy'
    })
  }

  return {
    mer,
    amer,
    nmer,
    recommendations
  }
}

/**
 * Validates input values for logical consistency.
 *
 * @param {CalculatorInputs} calculatorInputs - The calculator input values
 * @returns {Array<{type: 'warning', message: string}>} Array of validation messages
 */
export function validateInputs(calculatorInputs: CalculatorInputs): Array<{ type: 'warning'; message: string }> {
  const warnings: Array<{ type: 'warning'; message: string }> = []

  // Check if new customer revenue exceeds total revenue
  if (calculatorInputs.newCustomerRevenue > calculatorInputs.totalRevenue) {
    warnings.push({
      type: 'warning',
      message: 'New customer revenue should not exceed total revenue - please check your inputs'
    })
  }

  // Check if attributed revenue exceeds total revenue
  if (calculatorInputs.attributedRevenue > calculatorInputs.totalRevenue) {
    warnings.push({
      type: 'warning',
      message: 'Attributed revenue should not exceed total revenue - please check your inputs'
    })
  }

  // Check for zero marketing spend which causes division by zero
  if (calculatorInputs.totalMarketingSpend === 0) {
    warnings.push({
      type: 'warning',
      message: 'Marketing spend cannot be zero as it will result in undefined metrics - please enter a value greater than zero'
    })
  }

  return warnings
}
